import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SignIn, SignOut, Timer } from "@phosphor-icons/react";
import { toast } from "sonner";

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function Attendance() {
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [t, h] = await Promise.all([api.get("/attendance/today"), api.get("/attendance")]);
    setToday(t.data);
    setHistory(h.data);
  };
  useEffect(() => { load(); }, []);

  const act = async (path, msg) => {
    setBusy(true);
    try {
      await api.post(`/attendance/${path}`);
      toast.success(msg);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const checkedIn = !!today?.check_in;
  const checkedOut = !!today?.check_out;

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary">Presence</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-normal tracking-tight gradient-text mt-1">Attendance</h1>
      </div>

      <Card className="p-6 sm:p-8 rounded-md border-border">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-md bg-primary/10 flex items-center justify-center">
              <Timer size={28} weight="fill" className="text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {checkedIn ? `Checked in at ${fmtTime(today.check_in)}` : "You haven't checked in today"}
                {checkedOut && ` · Out at ${fmtTime(today.check_out)}`}
              </p>
              <p className="font-heading text-xl font-medium">
                {checkedOut ? "Day complete" : checkedIn ? "Currently working" : "Ready to start"}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button data-testid="check-in-btn" disabled={busy || checkedIn} onClick={() => act("check-in", "Checked in")}
              className="rounded-md bg-primary hover:bg-primary/90 transition-transform duration-200 hover:-translate-y-[1px]">
              <SignIn size={18} className="mr-1.5" /> Check In
            </Button>
            <Button data-testid="check-out-btn" disabled={busy || !checkedIn || checkedOut} onClick={() => act("check-out", "Checked out")}
              variant="outline" className="rounded-md border-border">
              <SignOut size={18} className="mr-1.5" /> Check Out
            </Button>
          </div>
        </div>
      </Card>

      <Card className="rounded-md border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50">
              <TableHead className="font-medium">Date</TableHead>
              <TableHead className="font-medium">Check In</TableHead>
              <TableHead className="font-medium">Check Out</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.length === 0 && (
              <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">No records yet.</TableCell></TableRow>
            )}
            {history.map((a) => (
              <TableRow key={a.id} className="hover:bg-secondary/30 transition-colors duration-200">
                <TableCell className="font-mono-num text-sm">{a.date}</TableCell>
                <TableCell className="font-mono-num text-sm">{fmtTime(a.check_in)}</TableCell>
                <TableCell className="font-mono-num text-sm">{fmtTime(a.check_out)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
